import cn from 'classnames';
import {BigNumber} from 'ethers';
import {parseUnits} from 'ethers/lib/utils';
import {useMemo, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {formatBigNumber} from '../../../lib/utilities';
import {useGetPictureUrlQuery} from '../../../redux/projects.api';
import {useBalanceOfQuery} from '../../../redux/token-contract.api';
import {selectWallet} from '../../../redux/wallet.slice';
import {TOKEN_LIST} from '../../constants';
import useApprove from '../../hooks/useApprove';
import useFrame from '../../hooks/useFrame';
import useInvestSubmit from '../../hooks/useInvestSubmit';
import useProjectById from '../../hooks/useProjectById';
import Button from '../button/button';
import Checkbox from '../checkbox/checkbox';
import Image from '../image/image';
import './qt-invest-confirm.sass';

type TProps = {
  className?: string;
};

function QtInvestConfirm({className}: TProps) {
  const {projectId: id} = useParams<{projectId: string}>();
  const {showSuccess} = useFrame();
  const {watch} = useFormContext();
  const amount = watch('amount');
  const {onSubmit} = useInvestSubmit(id);
  const {project, tokens} = useProjectById(id);

  const {data: pictureUrl} = useGetPictureUrlQuery(project!.tokenIcon!, {
    skip: !project?.tokenIcon,
  });

  const selectedToken = useMemo(() => {
    if (!tokens) return undefined;
    const tokenAddress = watch('token');
    const token = tokens.find(el => tokenAddress.toLowerCase() === el.address?.toLowerCase());
    return token;
  }, [tokens, watch]);

  const {approve, isApproved, refetchAllowance} = useApprove(amount, selectedToken);
  const [disabled, setDisabled] = useState(false);
  const [isAcceptTerms, setAcceptTerms] = useState(false);

  const exchangeRate = formatBigNumber(BigNumber.from(project?.exchageRate || '1'));

  const wallet = useSelector(selectWallet);

  const {data: balance, isFetching} = useBalanceOfQuery([selectedToken?.address, wallet.address]);
  const formattedAmount = useMemo(() => parseUnits(amount || '0', selectedToken?.decimals), [amount, selectedToken]);
  const isBalanceSufficient = useMemo(
    () => isFetching || balance?.gte(formattedAmount),
    [isFetching, balance, formattedAmount],
  );

  const receive = useMemo(() => {
    return formattedAmount.mul(parseUnits('1')).div(parseUnits(exchangeRate || '1')); // NOTE division by zero
  }, [exchangeRate, formattedAmount]);
  return (
    <div className={cn('qt-invest-confirm', className)}>
      <div className="qt-invest-confirm__title">Confirmation</div>
      <div className="qt-invest-confirm__item _mb-24">
        <div className="qt-invest-confirm__label">You invest</div>
        <div className="qt-invest-confirm__label">You will receive</div>
      </div>
      <div className="qt-invest-confirm__item _mb-36">
        <div className="qt-invest-confirm__amount">
          {selectedToken && (
            <img src={TOKEN_LIST[selectedToken.symbol.toLowerCase()].icon} alt={`${selectedToken.symbol} token`} />
          )}
          {formatBigNumber(formattedAmount)} {selectedToken?.symbol}
        </div>
        <div className="qt-invest-confirm__amount">
          {pictureUrl && <Image src={pictureUrl.fileUrl} alt={'tokenIcon'} />}
          {formatBigNumber(receive)} {project?.tgeToken}
        </div>
      </div>
      {!isBalanceSufficient && (
        <div className="qt-invest-confirm__item">
          <div className="qt-invest-confirm__error">There are not enough {selectedToken?.symbol} in your wallet</div>
        </div>
      )}
      <div className="qt-invest-confirm__item _mb-36">
        <Checkbox
          checked={isAcceptTerms}
          setChecked={val => {
            setAcceptTerms(val);
          }}
        >
          <div className="qt-invest-confirm__terms">
            I accept{' '}
            <a className="footer__menu-item" href="https://quicktoken.org/terms" target="_blank" rel="noreferrer">
              terms of the project
            </a>{' '}
            by clicking this button
          </div>
        </Checkbox>
      </div>
      <div className="qt-invest-confirm__item">
        <Button
          className="qt-invest-confirm__button"
          onClick={async () => {
            setDisabled(true);
            try {
              if (!isApproved) {
                !isApproved && (await approve());
                setDisabled(false);
              } else {
                await onSubmit();
                showSuccess();
                await refetchAllowance();
              }
            } catch (err) {
              setDisabled(false);
            }
          }}
          disabled={!isBalanceSufficient || disabled || !isAcceptTerms}
        >
          {isApproved ? 'Confirm' : 'Approve'}
        </Button>
      </div>
    </div>
  );
}

export default QtInvestConfirm;
